import { Button } from "@/components/ui/button";
import React from "react";
import { CardDescription } from "./card";
import { CardTitle } from "./dialog";
import { useTranslation } from "@/features/utils/i18n";

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  onReset?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

// A class component cannot call hooks: the fallback is its own component so
// that it reads the reader's language like everything else.
function ErrorFallback({
  message,
  onReset,
}: {
  message?: string;
  onReset: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="m-4 space-y-4">
      <CardTitle>{t("common.errorBoundary.title")}</CardTitle>
      <CardDescription className="space-y-2">
        <p className="text-base">{t("common.errorBoundary.description")}</p>
        {!!message && (
          <p className="text-xs text-red-500 font-mono">{message || ""}</p>
        )}
        <Button variant="link" size="sm" onClick={onReset} className="mt-2">
          {t("common.retry")}
        </Button>
      </CardDescription>
    </div>
  );
}

export class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    this.props.onReset?.();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorFallback
          message={this.state.error?.message}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}
